import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTranslation } from './translations';
import { useTheme } from './ThemeContext';

/**
 * API 拦截器组件
 * 监听全局未授权错误和token过期事件，自动重定向到登录页面
 */
const ApiInterceptor = ({ children }) => {
  const navigate = useNavigate();
  const { language } = useTheme();
  const t = getTranslation(language);
  
  useEffect(() => {
    // 错误消息的存储变量，防止多次显示相同错误
    let lastErrorTime = 0;
    
    // 重定向到登录页面的函数
    const redirectToLogin = (message) => {
      const currentTime = Date.now();
      // 防止频繁重定向（3秒内只重定向一次）
      if (currentTime - lastErrorTime < 3000) {
        return;
      }
      lastErrorTime = currentTime;
      
      console.warn('认证错误：', message);
      
      navigate('/dashboard/login', {
        state: { 
          from: window.location.pathname,
          authError: true,
          message: message || t('sessionExpired')
        }
      });
    };
    
    // 原始的全局错误处理函数
    const originalErrorHandler = window.onerror;
    
    // 全局 fetch API 错误监听函数
    const handleGlobalError = (event, ...args) => {
      const error = event.error || event.reason;
      
      // 检查是否为认证错误
      if (error && error.isAuthError === true && error.status === 401) {
        redirectToLogin(error.message);
        event.preventDefault();
        return true;
      }
      
      // 对于其他类型的错误，调用原始处理函数
      if (typeof originalErrorHandler === 'function') {
        return originalErrorHandler.call(window, event, ...args);
      }
      
      return false;
    };
    
    // 拦截未处理的 Promise 异常
    const handleUnhandledRejection = (event) => {
      const error = event.reason;
      if (error && error.isAuthError === true && error.status === 401) {
        redirectToLogin(error.message);
        event.preventDefault();
      }
    };
    
    // 监听token过期事件（由getAuthToken触发）
    const handleTokenExpired = (event) => {
      redirectToLogin(event.detail?.message || t('sessionExpired'));
    };
    
    // 跨标签页同步：监听其他标签页清除登录状态
    const handleStorageChange = (event) => {
      if (event.key === 'authData' && event.newValue === null) {
        console.log('[ApiInterceptor] 检测到其他标签页退出登录');
        redirectToLogin('已在其他标签页退出登录');
      }
    };
    
    // 注册错误处理器
    window.onerror = handleGlobalError;
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('token-expired', handleTokenExpired);
    window.addEventListener('storage', handleStorageChange);
    
    // 清理函数
    return () => {
      window.onerror = originalErrorHandler;
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('token-expired', handleTokenExpired);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [navigate, language, t]);
  
  // 拦截器不渲染任何内容，仅作为错误处理器
  return children;
};

export default ApiInterceptor; 